import React from 'react';
import PropTypes from 'prop-types';

import { Snippet } from 'react-instantsearch';

import SearchResult from '../SearchResult';

const cleanUrl = (type, url) => {
  if (!url) return url;

  if (type === 'image') {
    return url.replace('s3.amazonaws.com/developertool/', 'imgtravpromobile.imgix.net/');
  }
  return url;
};

// Some untranslated CMS content is left as a run of slashes (`//`, `///`, ...) instead of real text.
const isPlaceholderText = (value) => typeof value === 'string' && /^\/+$/.test(value.trim());

/**
 * @typedef {object} HitProps
 * @property {object} hit An algolia Hit object. Returned from algolia search.
 * @property {string} type The type of search result. Used to determine how to transform the hit.
 * @property {Function} onSelect A function to call when the search result is selected (primarily to close the modal)
 */

/**
 * An object passed to algolia Hits as a hitComponent to transform data to a searchResult based on the type of the algolia Hit.
 *
 * @param {HitProps} props Props
 * @returns {JSX.Element} A searchResult component.
 */
const Hit = ({ hit, type, onSelect, pagesTranslationKeys = { title: 'title', clean_text: 'clean_text' } }) => {
  let descriptionKey = '';
  let resultIcon = null;
  let resultBackground = '';
  let resultUrl = '';
  let resultType = '';
  let resultTitle = '';

  if (type === 'apps') {
    const { id: hitId, icon: hitIcon, splash_screen: hitBackground, name: hitTitle } = hit;

    resultIcon = hitIcon;
    resultBackground = cleanUrl('image', hitBackground);
    resultUrl = `/info/${hitId}`;
    resultTitle = hitTitle;
  }

  if (type === 'pages') {
    const { image: hitBackground, chapter_id: hitChapterId, number: hitPageNumber } = hit;
    resultBackground = cleanUrl('image', hitBackground);
    resultUrl = `/main/ret/chapter/${hitChapterId}/${hitPageNumber - 1}`;

    const localizedTitle = hit[pagesTranslationKeys?.title];
    resultTitle = isPlaceholderText(localizedTitle) ? hit.title : localizedTitle || hit.title;

    const localizedDescriptionKey = pagesTranslationKeys?.clean_text;
    descriptionKey = isPlaceholderText(hit[localizedDescriptionKey])
      ? 'clean_text'
      : localizedDescriptionKey || 'clean_text';
  }

  if (type === 'sales_companion_items') {
    const { image: hitBackground, url: hitUrl, type: hitType, title: hitTitle } = hit;
    resultBackground = cleanUrl('image', hitBackground);
    resultUrl = hitUrl;
    resultType = hitType;
    resultTitle = hitTitle;
  }

  const transformedHit = { ...hit };

  const hitDescription = hit?._snippetResult?.[descriptionKey]?.value;
  if (hitDescription) {
    transformedHit[descriptionKey] = hitDescription;
  }

  resultBackground += '?w=400';
  return (
    <SearchResult
      title={resultTitle}
      description={<Snippet hit={transformedHit} attribute={descriptionKey} />}
      icon={resultIcon}
      backgroundImage={resultBackground}
      url={resultUrl}
      onSelect={onSelect}
      type={type}
      hitType={resultType}
    />
  );
};

Hit.propTypes = {
  hit: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['apps', 'pages', 'sales_companion_items']).isRequired,
  onSelect: PropTypes.func.isRequired,
  pagesTranslationKeys: PropTypes.shape({
    title: PropTypes.string,
    clean_text: PropTypes.string,
  }),
};

export default Hit;
